class BwsStickyAtc extends HTMLElement {
  #controller;
  #frame;
  #observer;
  #scrollRoot;
  #sourceSection;
  #footerBoundary;
  #staticCta;
  #primaryButton;
  #addToCartComponent;
  #priceSource;
  #priceTarget;
  #stickyButton;
  #compareLink;
  #drawer;
  #drawerContent;
  #drawerOpen = false;
  #drawerClosing = false;
  #closeTimer;
  #movedControls = [];

  connectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#controller = new AbortController();
    this.#scrollRoot = this.closest('.content-for-layout');
    this.#priceTarget = this.querySelector('[data-bws-sticky-atc-price]');
    this.#stickyButton = this.querySelector('[data-bws-sticky-atc-trigger]');
    this.#compareLink = this.querySelector('[data-bws-sticky-atc-compare]');
    this.#connectToSource();

    const { signal } = this.#controller;
    document.addEventListener('scroll', this.#requestUpdate, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.#requestUpdate, { passive: true, signal });
    this.#stickyButton?.addEventListener('click', this.#handleClick, { signal });
    this.#compareLink?.addEventListener('click', this.#handleCompareClick, { signal });

    if (this.dataset.discontinued === 'true') {
      this.#setVisible(true);
      return;
    }

    document.addEventListener('click', this.#handleDrawerDismiss, { signal });
    document.addEventListener('keydown', this.#handleDrawerKeydown, { signal });
    document.addEventListener('shopify:section:load', this.#connectToSource, { signal });
  }

  disconnectedCallback() {
    this.#closeDrawer(false, true);
    this.#controller?.abort();
    this.#observer?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    if (this.#closeTimer) clearTimeout(this.#closeTimer);
  }

  #connectToSource = () => {
    this.#observer?.disconnect();
    this.#sourceSection = this.#scrollRoot?.querySelector('#bws-pdp-section-01');
    this.#footerBoundary = this.#findVisibleFooter();
    this.#drawer = this.#sourceSection?.querySelector('[data-bws-purchase-drawer]');
    this.#drawerContent = this.#drawer?.querySelector('[data-bws-purchase-drawer-content]');

    if (!this.#sourceSection) {
      this.#setVisible(false);
      return;
    }

    this.#observer = new MutationObserver(this.#syncSource);
    this.#observer.observe(this.#sourceSection, { attributes: true, childList: true, subtree: true });
    this.#syncSource();
  };

  #syncSource = () => {
    this.#staticCta = this.#sourceSection?.querySelector('.bws-product-information__cta');
    this.#primaryButton = this.#sourceSection?.querySelector('.bws-product-information__add-button');
    this.#addToCartComponent = this.#primaryButton?.closest('add-to-cart-component');
    this.#priceSource = this.#sourceSection?.querySelector('.bws-product-information__price [ref="priceContainer"]');

    if (this.#priceTarget && this.#priceSource) {
      const visiblePrice = this.#priceSource.cloneNode(true);
      visiblePrice.querySelectorAll('.visually-hidden').forEach((element) => element.remove());
      this.#priceTarget.textContent = visiblePrice.textContent.trim().replace(/\s+/g, ' ');
    }

    if (this.#stickyButton && this.#primaryButton) {
      this.#stickyButton.disabled = this.#isMobile() && !this.#drawerOpen ? false : this.#primaryButton.disabled;
      this.#stickyButton.textContent = this.#primaryButton.textContent.trim().replace(/\s+/g, ' ');
    }

    this.#requestUpdate();
  };

  #handleClick = () => {
    if (this.#drawerClosing) return;

    if (this.#isMobile() && !this.#drawerOpen) {
      this.#openDrawer();
      return;
    }

    if (!this.#primaryButton || this.#primaryButton.disabled) return;
    if (this.#drawerOpen) {
      this.#closeDrawer(false, false, this.#submitPrimary);
      return;
    }

    this.#submitPrimary();
  };

  #handleCompareClick = (event) => {
    if (!(this.#compareLink instanceof HTMLAnchorElement)) return;

    const targetId = this.#compareLink.hash.slice(1);
    const target = targetId ? document.getElementById(targetId) : null;
    if (!target) return;

    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });

    if (window.location.hash !== this.#compareLink.hash) {
      history.pushState(null, '', this.#compareLink.hash);
    }
  };

  #submitPrimary = () => {
    if (!this.#primaryButton || this.#primaryButton.disabled) return;

    const previousAnimationSetting = this.#addToCartComponent?.dataset.addToCartAnimation;

    if (previousAnimationSetting === 'true') {
      this.#animateFlyToCart();
      this.#addToCartComponent.dataset.addToCartAnimation = 'false';
    }

    this.#primaryButton.click();

    if (this.#addToCartComponent && previousAnimationSetting !== undefined) {
      this.#addToCartComponent.dataset.addToCartAnimation = previousAnimationSetting;
    }
  };

  #handleDrawerDismiss = (event) => {
    if (!this.#drawerOpen) return;

    const dismissControl = event.target instanceof Element
      ? event.target.closest('[data-bws-purchase-drawer-dismiss]')
      : null;
    if (dismissControl) this.#closeDrawer();
  };

  #handleDrawerKeydown = (event) => {
    if (!this.#drawerOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeDrawer();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = [
      ...this.#drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ),
      ...(this.#stickyButton && !this.#stickyButton.disabled ? [this.#stickyButton] : []),
    ].filter((element) => !element.hidden && element.getClientRects().length > 0);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #openDrawer() {
    if (!this.#drawer || !this.#drawerContent || !this.#sourceSection || this.#drawerClosing) return;

    const details = this.#sourceSection.querySelector('.bws-product-information-details__bottom');
    const controls = details
      ? [...details.children].filter((element) =>
          element.matches('variant-picker, .bws-product-information__supporting-details, .bws-product-information__purchase-options')
        )
      : [];

    this.#movedControls = controls.map((element) => ({
      element,
      parent: element.parentNode,
      nextSibling: element.nextSibling,
    }));
    controls.forEach((element) => this.#drawerContent.append(element));

    this.#drawerOpen = true;
    this.dataset.drawerOpen = 'true';
    this.#drawer.hidden = false;
    delete this.#drawer.dataset.closing;
    const backdrop = this.#sourceSection.querySelector('.bws-purchase-drawer__backdrop');
    if (backdrop) {
      backdrop.hidden = false;
      delete backdrop.dataset.closing;
    }
    this.#stickyButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('bws-purchase-drawer-open');
    this.#syncSource();

    void this.#drawer.offsetHeight;
    this.#drawer.dataset.open = 'true';
    if (backdrop) backdrop.dataset.open = 'true';
    requestAnimationFrame(() => this.#drawer.querySelector('.bws-purchase-drawer__close')?.focus());
  }

  #closeDrawer(restoreFocus = true, immediate = false, onClosed) {
    if (!this.#drawerOpen || (this.#drawerClosing && !immediate)) return;

    if (immediate && this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = undefined;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (immediate || reduceMotion || !this.#drawer) {
      this.#finalizeDrawerClose(restoreFocus, onClosed);
      return;
    }

    this.#drawerClosing = true;
    delete this.#drawer.dataset.open;
    this.#drawer.dataset.closing = 'true';
    const backdrop = this.#sourceSection?.querySelector('.bws-purchase-drawer__backdrop');
    if (backdrop) {
      delete backdrop.dataset.open;
      backdrop.dataset.closing = 'true';
    }
    this.#stickyButton?.setAttribute('aria-expanded', 'false');

    if (this.#closeTimer) clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      this.#closeTimer = undefined;
      this.#finalizeDrawerClose(restoreFocus, onClosed);
    }, 340);
  }

  #finalizeDrawerClose(restoreFocus, onClosed) {
    if (!this.#drawerOpen) return;

    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = undefined;
    }

    [...this.#movedControls].reverse().forEach(({ element, parent, nextSibling }) => {
      if (parent) parent.insertBefore(element, nextSibling);
    });
    this.#movedControls = [];
    this.#drawerOpen = false;
    this.#drawerClosing = false;
    delete this.dataset.drawerOpen;
    if (this.#drawer) {
      this.#drawer.hidden = true;
      delete this.#drawer.dataset.open;
      delete this.#drawer.dataset.closing;
    }
    const backdrop = this.#sourceSection?.querySelector('.bws-purchase-drawer__backdrop');
    if (backdrop) {
      backdrop.hidden = true;
      delete backdrop.dataset.open;
      delete backdrop.dataset.closing;
    }
    this.#stickyButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('bws-purchase-drawer-open');
    this.#syncSource();
    if (restoreFocus) this.#stickyButton?.focus();
    onClosed?.();
  }

  #isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  #findVisibleFooter() {
    const footers = document.querySelectorAll('.bws-pdp-footer-wrapper, .page-wrapper > footer');
    return [...footers].find((footer) => footer.getClientRects().length > 0);
  }

  #animateFlyToCart() {
    const image = this.#addToCartComponent?.dataset.productVariantMedia;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!image || !cartIcon || !this.#stickyButton) return;

    const flyToCart = document.createElement('fly-to-cart');
    flyToCart.classList.add('fly-to-cart--main', 'bws-fly-to-cart--sticky');
    flyToCart.style.setProperty('background-image', `url(${image})`);
    flyToCart.style.setProperty('--start-opacity', '0');
    flyToCart.source = this.#stickyButton;
    flyToCart.destination = cartIcon;
    document.body.append(flyToCart);
  }

  #requestUpdate = () => {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = undefined;
      this.#updateVisibility();
    });
  };

  #updateVisibility() {
    if (!this.#staticCta || !this.#scrollRoot) {
      this.#setVisible(false);
      return;
    }

    if (this.#drawerOpen && !this.#isMobile()) this.#closeDrawer(false, true);
    if (this.#drawerOpen) {
      this.#setVisible(true);
      return;
    }

    const rootBounds = this.#scrollRoot.getBoundingClientRect();
    const headerBottom = document.querySelector('#header-component')?.getBoundingClientRect().bottom ?? rootBounds.top;
    const visibleTop = Math.max(rootBounds.top, headerBottom);
    const hasPassedStaticCta = this.#staticCta.getBoundingClientRect().bottom <= visibleTop;
    if (!this.#footerBoundary?.getClientRects().length) this.#footerBoundary = this.#findVisibleFooter();

    const viewportBottom = Math.min(window.innerHeight, rootBounds.bottom);
    const hasReachedFooter = this.#footerBoundary?.getBoundingClientRect().top <= viewportBottom;
    this.dataset.footerReached = String(Boolean(hasReachedFooter));
    this.#setVisible(hasPassedStaticCta && !hasReachedFooter);
  }

  #setVisible(isVisible) {
    this.dataset.visible = String(isVisible);
    this.inert = !isVisible;
    this.setAttribute('aria-hidden', String(!isVisible));
  }
}

if (!customElements.get('bws-sticky-atc')) {
  customElements.define('bws-sticky-atc', BwsStickyAtc);
}
